import {
  AGENT_RECURSION_LIMIT,
  AVAILABLE_MODELS,
  ModelId,
  theme,
} from "@config";
import { useCreateTaskState, useUpdateTaskState } from "@hooks";
import { Device, RoadmapTask, TaskManualStatus, TaskState } from "@models";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Card,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  DocStatus,
  DocumentStatus,
  DocumentUIStatus,
  TemplateConfig,
} from "@types";
import {
  buildPrompt,
  captureSentryException,
  getDocDefaultTitleByKey,
  getDocUIStatus,
  getTaskStatusInfo,
  normalizeTaskManualStatus,
} from "@utils";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DocWarnings } from "src/components/DocViewer/components/DocWarnings";
import { TaskMoreOptions } from "src/components/Roadmap/Task/TaskMoreOptions";
import { AssigneeButton } from "src/components/Tasks/AssigneeButton";
import { TaskDueDateChip } from "src/components/Tasks/TaskDueDateChip";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import type { AgentThread } from "src/stores/agent";
import { User } from "src/stores/models/user";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { v4 as uuidv4 } from "uuid";

import { useStore } from "../../../stores/index.ts";

type Props = {
  task: RoadmapTask;
  docStatus?: DocStatus;
  isLoading: boolean;
  onClick: () => void;
  incompleteTemplates?: string[];
  device: Device;
  templatesConfig: TemplateConfig;
  user: User;
  tasks: RoadmapTask[];
  taskInstance?: TaskState;
};

type HumanMessageInput = {
  id: string;
  type: "human";
  content: Array<{ type: "text"; text: string }>;
};

export const TaskAccordionCard = ({
  task,
  docStatus,
  isLoading,
  onClick,
  incompleteTemplates,
  templatesConfig,
  user,
  tasks,
  taskInstance,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [deadlinePickerValue, setDeadlinePickerValue] = useState<Dayjs | null>(
    taskInstance?.dueDate ? dayjs(taskInstance.dueDate) : null
  );
  const { deviceId = "", orgId = "" } = useParams();
  const navigate = useNavigate();

  // Mutations for assignee changes
  const createTaskState = useCreateTaskState({ orgId, deviceId });
  const updateTaskState = useUpdateTaskState({ orgId, deviceId });

  const handleAssigneeChange = async (assigneeId: string | null) => {
    // If task has an instance (state exists), update it
    if (taskInstance?.id) {
      await updateTaskState.mutateAsync({
        stateId: taskInstance.id,
        data: { assigneeId },
      });
    } else {
      // Create a new task state with the assignee
      await createTaskState.mutateAsync({
        taskKey: task.taskKey,
        assigneeId,
      });
    }
  };

  useEffect(() => {
    setDeadlinePickerValue(
      taskInstance?.dueDate ? dayjs(taskInstance.dueDate) : null
    );
  }, [taskInstance?.dueDate]);

  const mutateDueDate = async (dueDate: string | null) => {
    if (taskInstance?.id) {
      await updateTaskState.mutateAsync({
        stateId: taskInstance.id,
        data: { dueDate },
      });
      return;
    }

    await createTaskState.mutateAsync({
      taskKey: task.taskKey,
      dueDate,
    });
  };

  const handleDeadlineChange = async (newDate: Dayjs | null) => {
    if (isTaskLocked) return;
    if (newDate && !newDate.isValid()) return;

    setDeadlinePickerValue(newDate);
    await mutateDueDate(newDate?.toISOString() ?? null);
  };

  const isManualTask = task.taskConfig.taskType === "MANUAL";
  // Only allow expand for document tasks with descriptions (not manual tasks)
  const canExpand = !isManualTask && task.description;

  const { agentStore } = useStore();
  const backendUrl = window._env_.BACKEND_BASE_URL;
  const [thread, setThread] = useState<AgentThread | null>(null);
  const { id: userId } = user;

  // Use one vetted model for now until we are certain that we can switch models and reasoning efforts dynamically for this task.
  const selectedModelConfig = AVAILABLE_MODELS[ModelId.GPT_6_1_SOL];
  const selectedReasoningEffort = "high";

  const handleOpenMoreOptions = async () => {
    setIsMoreOptionsOpen(true);
    setThread(await agentStore.getRoadmapThread(`${deviceId}/${task.id}`));
  };

  const handleCloseMoreOptions = () => {
    setIsMoreOptionsOpen(false);
  };

  const handleSubmit = async (
    e: React.MouseEvent,
    deviceId: string,
    orgId: string,
    task: RoadmapTask
  ) => {
    e.preventDefault();
    e.stopPropagation();

    agentStore.navigateToThread(null);

    const docTitle = getDocDefaultTitleByKey(task.taskKey, templatesConfig);

    const threadId = await agentStore.createNewThread(orgId, userId, {
      name: `Agent run (${docTitle})`,
      roadmap_task: `${deviceId}/${task.id}`,
    });

    // Build the message text with the input and referenced context
    const messageText = buildPrompt({
      instructions: `Create the answers for all data-keys of the ${task.taskKey} template. Use existing documents and data keys as context.`,
      additionalContext: `<current-url>\n${window.location.href}\n</current-url>`,
    });

    const newMessage: HumanMessageInput = {
      id: uuidv4(),
      type: "human",
      content: [{ type: "text", text: messageText }],
    };

    await agentStore.createBackgroundRun(threadId, {
      input: { messages: [newMessage] },
      metadata: {
        organization_id: orgId,
        user_id: userId,
      },
      context: {
        organization_id: orgId,
        user_id: userId,
        be_url: backendUrl,
        model_provider: selectedModelConfig.provider,
        model_name: selectedModelConfig.modelName,
        temperature: selectedModelConfig.temperature,
        max_output_tokens: selectedModelConfig.maxOutputTokens,
        context_window: selectedModelConfig.contextWindow,
        context_summarization_threshold:
          selectedModelConfig.contextSummarizationThreshold,
        reasoning_effort:
          selectedReasoningEffort || selectedModelConfig.reasoningEffort,
      },
      config: {
        recursion_limit: AGENT_RECURSION_LIMIT,
      },
      checkpoint: undefined,
      streamMode: ["messages", "messages-tuple", "values"],
      streamResumable: true,
      streamSubgraphs: true,
      onDisconnect: "continue",
    });

    const thread = await agentStore.client.threads.get(threadId);

    if (!thread) {
      captureSentryException(`Thread not found`, {
        extra: { threadId, deviceId, orgId, userId, taskKey: task.taskKey },
      });
      throw new Error(`Thread not found for threadId ${threadId}`);
    }

    setThread(thread);
    return thread;
  };

  const docUIStatus = getDocUIStatus(task);

  const isTaskLocked = docUIStatus === DocumentUIStatus.LOCKED;
  const templateConfig = templatesConfig[task.taskKey];
  const isUserUpload = templateConfig?.userUpload ?? false;
  const shouldShowDelegateAction =
    !isTaskLocked && !isUserUpload && !isManualTask && !!templateConfig;

  const shouldShowMoreOptions = true;

  const manualStatus = normalizeTaskManualStatus(
    taskInstance?.manualStatus ?? TaskManualStatus.TODO
  );
  const documentStatus = docUIStatus ?? docStatus ?? DocumentStatus.AVAILABLE;
  const taskStatusInfo = isManualTask
    ? getTaskStatusInfo({ taskType: "MANUAL", status: manualStatus })
    : getTaskStatusInfo({ taskType: "DOCUMENT", status: documentStatus });

  const tooltipText = "Enroll in the beta to unlock this task.";

  const handleClick = () => {
    if (isTaskLocked || isMoreOptionsOpen) return;

    // For MANUAL tasks, always navigate to task detail view
    if (isManualTask) {
      // Use taskInstance.id if available, otherwise use taskKey as fallback
      const taskIdentifier = taskInstance?.id || task.taskKey;
      navigate(
        fillRouteWithSlugs(ROUTES.TASK_DETAIL, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
          [ROUTE_SLUGS.TASK_ID]: taskIdentifier,
        })
      );
    } else {
      onClick();
    }
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <Tooltip
      title={tooltipText}
      followCursor
      {...(!isTaskLocked && { open: false })}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 6],
              },
            },
          ],
        },
      }}
    >
      <div
        className="flex items-center w-full"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative w-full">
          <Card
            data-testid={task.taskKey}
            id={task.taskKey}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${taskStatusInfo.borderColor}`,
              boxShadow: "none",
              padding: "10px",
              backgroundColor: "transparent",
              "&:hover": {
                background: `${theme.palette.grey[100]}`,
              },
              "& .doc-warnings-container": {
                maxHeight: 0,
                overflow: "hidden",
                transition: "max-height 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              },
              "& .doc-warnings-container.expanded": {
                maxHeight: "500px", // Large enough value to accommodate content
              },
            }}
          >
            <div className="flex items-center w-full gap-2 justify-between">
              <div
                className="flex items-center w-full gap-2"
                data-testid="task-card-row"
              >
                {isLoading ? (
                  <CircularProgress size={21} />
                ) : (
                  taskStatusInfo.icon
                )}
                <div>{task.title}</div>
              </div>

              <div
                className="flex items-center gap-1"
                data-testid="task-card-actions"
              >
                {canExpand && (
                  <IconButton
                    size="small"
                    onClick={handleExpandToggle}
                    sx={{ p: 0.5 }}
                  >
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
                <TaskDueDateChip dueDate={taskInstance?.dueDate} />
                {/* Assignee button */}
                <AssigneeButton
                  assignee={taskInstance?.assignee ?? null}
                  onAssigneeChange={handleAssigneeChange}
                />
                {shouldShowMoreOptions && (
                  <TaskMoreOptions
                    deviceId={deviceId}
                    handleOpenOptions={handleOpenMoreOptions}
                    handleCloseOptions={handleCloseMoreOptions}
                    handleSubmit={handleSubmit}
                    task={task}
                    thread={thread}
                    showDelegateAction={shouldShowDelegateAction}
                    showDeadlineAction
                    dueDate={deadlinePickerValue}
                    onDueDateChange={(newDate) => {
                      void handleDeadlineChange(newDate);
                    }}
                    onDueDateClear={() => {
                      void handleDeadlineChange(null);
                    }}
                    isDeadlineDisabled={isTaskLocked}
                    disabledTooltip="Deadline cannot be set for locked tasks"
                  />
                )}
              </div>
            </div>
            <div
              className={`doc-warnings-container ${isHovered ? "expanded" : ""}`}
            >
              {incompleteTemplates && incompleteTemplates.length > 0 && (
                <div className="-m-2 mt-2">
                  <DocWarnings
                    templatesConfig={templatesConfig}
                    incompleteTemplates={incompleteTemplates}
                    size="small"
                    customTitle={`This task has limited context because the following document${incompleteTemplates.length > 1 ? "s are " : " is "} missing or incomplete:`}
                    deviceId={deviceId}
                    tasks={tasks}
                  />
                </div>
              )}
            </div>

            {/* Expandable content section - only for document tasks with descriptions */}
            {canExpand && (
              <Collapse in={isExpanded}>
                <div className="pt-2 text-sm text-gray-600">
                  {task.description}
                </div>
              </Collapse>
            )}
          </Card>
        </div>
      </div>
    </Tooltip>
  );
};
